import { formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  LOCALE_ID,
  signal,
} from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';

import { ActivationLinkService } from '../activation-link.service';
import { parseLifecycleInstant } from '../tenant-lifecycle';
import { TenantLifecycleService } from '../tenant-lifecycle.service';
import {
  ACTIVATION_INTERVALS,
  ACTIVATION_PLANS,
  activationAvailability,
  ActivationCheckout,
  ActivationConfirmResult,
  ActivationInterval,
  ActivationOption,
  ActivationPlan,
  ActivationSelection,
  findActivationOption,
  formatActivationAmount,
  formatCardExpiry,
  formatPaymentMethodLabel,
  initialActivationSelection,
} from './activation-checkout';
import { ActivationCheckoutError, ActivationCheckoutService } from './activation-checkout.service';
import { activationReturnUrl, ActivationRedirect, clearPendingPrice, storePendingPrice } from './activation-redirect';
import {
  PAYMENT_ELEMENT_HOST_REGISTRY,
  PaymentElementHostDirective,
  PaymentElementHostRegistry,
} from './payment-element-host.directive';
import { STRIPE_LOADER, StripeSetupForm } from './stripe-setup-form';

export interface ActivationDialogData {
  /** A fresh answer of GET api/v1/tenant/activation; null makes the modal load it. */
  checkout: ActivationCheckout | null;
  /** Set when the browser comes back from a redirect-based confirmation. */
  redirect: ActivationRedirect | null;
}

export type ActivationDialogView =
  | 'loading'
  | 'handoff'
  | 'saved'
  | 'plan'
  | 'payment'
  | 'processing'
  | 'scheduled'
  | 'activated'
  | 'payment-failed'
  | 'blocked';

/** `contact`: book a meeting. `link`: the activation link flow from before the modal. */
export type ActivationHandoff = 'contact' | 'link';

export interface TranslatedText {
  key: string;
  params: Record<string, string>;
}

const T = 'TenantLifecycle.Checkout';

const CONFLICT_KEYS = {
  workspace_gone: `${T}.Errors.WorkspaceGone`,
  subscription_ended: `${T}.Errors.SubscriptionEnded`,
  setup_incomplete: `${T}.Errors.SetupIncomplete`,
  price_unavailable: `${T}.Errors.PriceUnavailable`,
  payment_method_unavailable: `${T}.Errors.PaymentMethodUnavailable`,
} as const;

/** Whether the click that opened the modal still allows a new tab (no popup blocker). */
function hasUserActivation(): boolean {
  const activation = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
  return activation?.isActive === true;
}

/**
 * "Plan aktivieren" in the admin console. With a payment method saved at
 * signup it opens on one click: plan, saved method, "Plan aktivieren"
 * (`saved` view, confirm-saved). Otherwise, and after "Andere Zahlungsmethode"
 * or "Plan ändern": choose plan, interval and the private RSS feed, then pay
 * with a new method in the Stripe Payment Element. Opened by
 * ActivationFlowService for Company Administrators.
 */
@Component({
  selector: 'hypecast-admin-activation-dialog',
  templateUrl: './activation-dialog.component.html',
  styleUrl: './activation-dialog.component.scss',
  standalone: true,
  imports: [
    MatButtonToggleModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    PaymentElementHostDirective,
    TranslatePipe,
  ],
  providers: [{ provide: PAYMENT_ELEMENT_HOST_REGISTRY, useExisting: forwardRef(() => ActivationDialogComponent) }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivationDialogComponent implements PaymentElementHostRegistry {
  private readonly dialogRef =
    inject<MatDialogRef<ActivationDialogComponent, ActivationConfirmResult | null>>(MatDialogRef);
  private readonly data = inject<ActivationDialogData>(MAT_DIALOG_DATA);
  private readonly checkoutService = inject(ActivationCheckoutService);
  private readonly linkService = inject(ActivationLinkService);
  private readonly lifecycle = inject(TenantLifecycleService);
  private readonly loadStripe = inject(STRIPE_LOADER);
  private readonly locale = inject(LOCALE_ID, { optional: true }) ?? 'en-US';

  protected readonly plans = ACTIVATION_PLANS;
  protected readonly intervals = ACTIVATION_INTERVALS;

  protected readonly view = signal<ActivationDialogView>('loading');
  protected readonly handoff = signal<ActivationHandoff>('link');
  protected readonly checkout = signal<ActivationCheckout | null>(null);
  protected readonly selection = signal<ActivationSelection>({ plan: 'medium', interval: 'month', rss: false });
  protected readonly useNewCard = signal(false);
  protected readonly busy = signal(false);
  protected readonly stripeReady = signal(false);
  /** Stripe's own message (already in the user's language), shown under the Payment Element. */
  protected readonly paymentError = signal<string | null>(null);
  /** Translation key of our own error message. */
  protected readonly errorKey = signal<string | null>(null);
  protected readonly result = signal<ActivationConfirmResult | null>(null);

  protected readonly option = computed<ActivationOption | null>(() => {
    const checkout = this.checkout();
    return checkout === null ? null : findActivationOption(checkout.options, this.selection());
  });

  protected readonly availability = computed(() =>
    activationAvailability(this.checkout()?.options ?? [], this.selection()),
  );

  protected readonly price = computed<TranslatedText | null>(() => {
    const option = this.option();
    return option === null ? null : this.priceText(option);
  });

  /** "Visa •••• 4242, gültig bis 12/30", "SEPA-Lastschrift DE •••• 3000", ... */
  protected readonly savedMethod = computed((): TranslatedText | null => {
    const method = this.checkout()?.paymentMethod ?? null;
    if (method === null) {
      return null;
    }
    const label = formatPaymentMethodLabel(method);
    const expiry = formatCardExpiry(method);
    switch (method.type) {
      case 'sepa_debit':
        return { key: `${T}.SavedSepa`, params: { account: label } };
      case 'us_bank_account':
        return { key: `${T}.SavedBankAccount`, params: { account: label } };
      default:
        return expiry === null
          ? { key: `${T}.SavedCardNoExpiry`, params: { card: label } }
          : { key: `${T}.SavedCard`, params: { card: label, expiry } };
    }
  });

  protected readonly savedMethodIcon = computed(() =>
    this.checkout()?.paymentMethod?.type === 'card' ? 'credit_card' : 'account_balance',
  );

  /** When the trial ends, formatted; null when not trialing or unknown. */
  private readonly trialEnd = computed<string | null>(() => {
    const checkout = this.checkout();
    const date = parseLifecycleInstant(checkout?.trialEndsAt);
    return checkout?.status === 'trialing' && date !== null ? formatDate(date, 'longDate', this.locale) : null;
  });

  /** One-click view: billing starts when the trial ends (it is not cut short), otherwise today. */
  protected readonly billedFrom = computed((): TranslatedText => {
    const date = this.trialEnd();
    if (date !== null) {
      return { key: `${T}.BilledFrom`, params: { date } };
    }
    const key = this.checkout()?.status === 'trialing' ? `${T}.BilledFromTrialEnd` : `${T}.BilledFromToday`;
    return { key, params: {} };
  });

  /** The line under the primary button: when and how much we charge. */
  protected readonly chargeLine = computed<TranslatedText | null>(() => {
    const option = this.option();
    if (option === null) {
      return null;
    }
    const price = formatActivationAmount(option.amount, option.currency, this.locale);
    const date = this.trialEnd();
    const params: Record<string, string> = date === null ? { price } : { price, date };
    return { key: date === null ? `${T}.ChargeToday` : `${T}.ChargeTrial`, params };
  });

  protected readonly successText = computed<TranslatedText>(() => {
    if (this.view() === 'activated') {
      return { key: `${T}.Activated`, params: {} };
    }
    const plan = this.planName(this.selection().plan);
    const date = this.trialEnd();
    const params: Record<string, string> = date === null ? { plan } : { plan, date };
    return { key: date === null ? `${T}.ScheduledNoDate` : `${T}.Scheduled`, params };
  });

  private form: StripeSetupForm | null = null;
  private formLoading: Promise<void> | null = null;
  private host: HTMLElement | null = null;
  private mountedOn: HTMLElement | null = null;
  private destroyed = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      this.form?.destroy();
      this.form = null;
    });

    if (this.data.checkout !== null) {
      this.start(this.data.checkout);
    } else {
      void this.load();
    }
  }

  /** Called by PaymentElementHostDirective when the payment step shows its container. */
  attachPaymentHost(host: HTMLElement): void {
    this.host = host;
    // Mounting changes signals; not while the view is being checked.
    queueMicrotask(() => this.syncMount());
  }

  detachPaymentHost(host: HTMLElement): void {
    if (this.host === host) {
      this.host = null;
      queueMicrotask(() => this.syncMount());
    }
  }

  protected planName(plan: ActivationPlan): string {
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  }

  /** Price of a plan with the chosen interval and RSS setting, for the plan cards. */
  protected planPrice(plan: ActivationPlan): TranslatedText | null {
    const checkout = this.checkout();
    const option = checkout === null ? null : findActivationOption(checkout.options, { ...this.selection(), plan });
    return option === null ? null : this.priceText(option);
  }

  protected selectPlan(plan: ActivationPlan): void {
    if (this.availability().plans[plan]) {
      this.selection.update((selection) => ({ ...selection, plan }));
      this.errorKey.set(null);
    }
  }

  protected selectInterval(interval: ActivationInterval): void {
    if (this.availability().intervals[interval]) {
      this.selection.update((selection) => ({ ...selection, interval }));
      this.errorKey.set(null);
    }
  }

  protected setRss(rss: boolean): void {
    if (rss !== this.selection().rss && this.availability().rssToggle) {
      this.selection.update((selection) => ({ ...selection, rss }));
      this.errorKey.set(null);
    }
  }

  /** "Weiter" on the plan step: one click with the saved method, else the Payment Element. */
  protected toPayment(): void {
    if (this.option() === null) {
      return;
    }
    this.errorKey.set(null);
    if (this.useSavedMethod()) {
      this.view.set('saved');
      return;
    }
    this.view.set('payment');
    void this.ensureForm();
  }

  protected toPlan(): void {
    this.errorKey.set(null);
    this.paymentError.set(null);
    this.view.set('plan');
  }

  protected useOtherCard(): void {
    this.errorKey.set(null);
    this.useNewCard.set(true);
    this.view.set('payment');
    void this.ensureForm();
  }

  /** Back from the Payment Element to the one-click view with the saved method. */
  protected useSavedCard(): void {
    this.paymentError.set(null);
    this.errorKey.set(null);
    this.useNewCard.set(false);
    this.view.set('saved');
  }

  /** Primary button: books the selected price with the saved method or the new one. */
  protected async activate(): Promise<void> {
    const option = this.option();
    if (this.busy() || option === null) {
      return;
    }
    this.setBusy(true);
    this.errorKey.set(null);
    this.paymentError.set(null);
    try {
      if (this.useSavedMethod()) {
        const method = this.checkout()?.paymentMethod ?? null;
        await this.finish(this.checkoutService.confirmSaved(method?.id ?? null, option.priceId));
        return;
      }
      const setupIntentId = await this.confirmNewCard(option.priceId);
      if (setupIntentId === null) {
        return;
      }
      await this.confirm(option.priceId, setupIntentId);
    } catch (error: unknown) {
      this.handleError(error);
    } finally {
      this.setBusy(false);
    }
  }

  protected openInvoice(): void {
    const url = this.result()?.invoiceUrl;
    if (url) {
      this.linkService.openUrl(url);
    }
  }

  /** Button of the hand-off view, for when the modal could not open the tab by itself. */
  protected continueHandoff(): void {
    if (this.handoff() === 'contact') {
      const url = this.checkout()?.contactUrl;
      if (url) {
        this.linkService.openUrl(url);
      }
    } else {
      void this.linkService.open();
    }
    this.dialogRef.close(null);
  }

  protected close(): void {
    this.dialogRef.close(this.result());
  }

  private async load(): Promise<void> {
    this.view.set('loading');
    try {
      this.start(await this.checkoutService.load());
    } catch (error: unknown) {
      if (error instanceof ActivationCheckoutError && error.failure === 'forbidden') {
        this.linkService.notify('forbidden');
        this.dialogRef.close(null);
        return;
      }
      this.startHandoff('link');
    }
  }

  private start(checkout: ActivationCheckout): void {
    this.checkout.set(checkout);
    if (checkout.mode === 'contact') {
      this.linkService.rememberKind('contact');
      this.startHandoff('contact');
      return;
    }
    if (checkout.mode === 'unavailable') {
      this.startHandoff('link');
      return;
    }
    this.linkService.rememberKind('activate');
    this.selection.set(initialActivationSelection(checkout));
    this.useNewCard.set(checkout.paymentMethod === null);

    const redirect = this.data.redirect;
    if (redirect !== null) {
      void this.finishRedirect(redirect);
      return;
    }
    // A method saved at signup: one click on "Plan aktivieren".
    this.view.set(checkout.paymentMethod === null ? 'plan' : 'saved');
  }

  private useSavedMethod(): boolean {
    return !this.useNewCard() && (this.checkout()?.paymentMethod ?? null) !== null;
  }

  /** Contact or link flow. Opens it right away while the click still allows a new tab. */
  private startHandoff(handoff: ActivationHandoff): void {
    this.handoff.set(handoff);
    if (hasUserActivation()) {
      this.continueHandoff();
      return;
    }
    this.view.set('handoff');
  }

  private async finishRedirect(redirect: ActivationRedirect): Promise<void> {
    const checkout = this.checkout();
    const pending = redirect.priceId === null ? null : checkout?.options.find((o) => o.priceId === redirect.priceId);
    if (pending) {
      this.selection.set({ plan: pending.plan, interval: pending.interval, rss: pending.rss });
    }
    if (!redirect.succeeded || !pending) {
      clearPendingPrice();
      if (!redirect.succeeded) {
        this.useOtherCard();
        this.errorKey.set(`${T}.Errors.RedirectFailed`);
      } else {
        this.view.set('plan');
      }
      return;
    }
    this.view.set('processing');
    this.setBusy(true);
    try {
      await this.confirm(pending.priceId, redirect.setupIntentId);
    } catch (error: unknown) {
      this.handleError(error);
    } finally {
      this.setBusy(false);
    }
  }

  /** Stripe part of a new card. Returns the SetupIntent id, or null after showing why it failed. */
  private async confirmNewCard(priceId: string): Promise<string | null> {
    const form = this.form;
    if (form === null || !this.stripeReady()) {
      return null;
    }
    const invalid = await form.submit();
    if (invalid !== null) {
      this.paymentError.set(invalid || null);
      this.errorKey.set(invalid ? null : `${T}.Errors.CardRejected`);
      return null;
    }
    const clientSecret = await this.checkoutService.createSetupIntent();
    storePendingPrice(priceId);
    const outcome = await form.confirm(clientSecret, activationReturnUrl(window.location.href));
    if ('error' in outcome) {
      clearPendingPrice();
      this.paymentError.set(outcome.error || null);
      this.errorKey.set(outcome.error ? null : `${T}.Errors.CardRejected`);
      return null;
    }
    return outcome.setupIntentId;
  }

  private async confirm(priceId: string, setupIntentId: string | null): Promise<void> {
    await this.finish(this.checkoutService.confirm(priceId, setupIntentId));
  }

  private async finish(confirmation: Promise<ActivationConfirmResult>): Promise<void> {
    const result = await confirmation;
    clearPendingPrice();
    this.result.set(result);
    if (result.result === 'payment_failed') {
      this.view.set('payment-failed');
      return;
    }
    this.checkoutService.invalidate();
    this.lifecycle.applyActivation(result.result);
    this.view.set(result.result);
  }

  private handleError(error: unknown): void {
    if (!(error instanceof ActivationCheckoutError)) {
      this.errorKey.set('TenantLifecycle.ActivationUnavailable');
      this.backToFormIfProcessing();
      return;
    }
    switch (error.reason) {
      case 'workspace_gone':
      case 'subscription_ended':
        this.errorKey.set(CONFLICT_KEYS[error.reason]);
        this.view.set('blocked');
        return;
      case 'setup_incomplete':
      case 'payment_method_unavailable':
        // The saved method cannot be used (any more): enter a new one.
        this.useOtherCard();
        this.errorKey.set(CONFLICT_KEYS[error.reason]);
        return;
      case 'price_unavailable':
        this.errorKey.set(CONFLICT_KEYS.price_unavailable);
        this.view.set('plan');
        void this.reloadOptions();
        return;
      default:
        this.errorKey.set(
          error.failure === 'forbidden'
            ? 'TenantLifecycle.ActivationForbidden'
            : 'TenantLifecycle.ActivationUnavailable',
        );
        this.backToFormIfProcessing();
    }
  }

  private backToFormIfProcessing(): void {
    if (this.view() === 'processing') {
      this.view.set('payment');
    }
  }

  /** After price_unavailable: ask for the prices again, keeping the choice where it still exists. */
  private async reloadOptions(): Promise<void> {
    this.checkoutService.invalidate();
    try {
      const checkout = await this.checkoutService.load();
      if (checkout.mode !== 'checkout') {
        return;
      }
      const selection = this.selection();
      this.checkout.set(checkout);
      if (findActivationOption(checkout.options, selection) === null) {
        this.selection.set(initialActivationSelection(checkout));
      }
    } catch {
      // The old prices stay; the message already asks to pick another one.
    }
  }

  private async ensureForm(): Promise<void> {
    const checkout = this.checkout();
    if (this.form !== null || checkout?.publishableKey == null) {
      this.syncMount();
      return;
    }
    this.formLoading ??= this.createForm(checkout.publishableKey, checkout);
    await this.formLoading;
  }

  private async createForm(publishableKey: string, checkout: ActivationCheckout): Promise<void> {
    try {
      const form = await StripeSetupForm.create(this.loadStripe, {
        publishableKey,
        currency: checkout.currency,
        locale: this.locale,
        origin: window.location.origin,
      });
      if (form === null) {
        throw new Error('stripe_unavailable');
      }
      if (this.destroyed) {
        form.destroy();
        return;
      }
      form.onReadyChange((ready) => this.stripeReady.set(ready));
      form.onLoadError((message) => this.paymentError.set(message || null));
      this.form = form;
      this.syncMount();
    } catch {
      this.formLoading = null;
      this.errorKey.set(`${T}.Errors.StripeUnavailable`);
    }
  }

  private syncMount(): void {
    const form = this.form;
    if (form === null || this.host === this.mountedOn) {
      return;
    }
    if (this.mountedOn !== null) {
      form.unmount();
      this.mountedOn = null;
    }
    if (this.host !== null) {
      form.mount(this.host);
      this.mountedOn = this.host;
    }
  }

  private setBusy(busy: boolean): void {
    this.busy.set(busy);
    // No closing by backdrop or Escape while a payment is on its way.
    this.dialogRef.disableClose = busy;
  }

  private priceText(option: ActivationOption): TranslatedText {
    return {
      key: option.interval === 'year' ? `${T}.PerYear` : `${T}.PerMonth`,
      params: { price: formatActivationAmount(option.amount, option.currency, this.locale) },
    };
  }
}
